// GridAnimatorEffects.js

const objArray = {};

/**
 * Animates a grid pattern by drawing cells that overlap text content.
 * Trims the text canvas to just the drawn content before testing overlaps.
 *
 * @param {Array}  pattern       Array of cell objects (expects .normDelay, x, y, shape, etc.)
 * @param {number} patternSize   Total pattern size (for cycle detection)
 * @param {HTMLCanvasElement} textCanvas  The canvas containing text
 * @param {number} fraction      0..1 fraction to filter pattern by normDelay
 * @param {string} brushColor    Fill color for shapes
 * @param {number} canvasWidth   Offscreen canvas width
 * @param {number} canvasHeight  Offscreen canvas height
 * @param {string} id            Cache key for the offscreen obj
 * @returns {HTMLCanvasElement|null}
 */
export function animateGridPattern(
  pattern,
  patternSize,
  textCanvas,
  fraction,
  brushColor,
  canvasWidth,
  canvasHeight,
  id,
  textBounds
) {
  if (!pattern || !textCanvas) return null;

  let obj = objArray[id];
  if (!obj) {
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    obj = {
      canvas,
      ctx: canvas.getContext('2d'),
      laststreamSize: null,
      aniStream: [],
      prevAniStream: [],
      lastFraction: null,
      localDrawStream: {},
      activeBuffer: new Set(),
      patternSize,
      aniIndex: 0,
      shapeDrawMap: {},
      cycleCount: 0,
      lastRawFraction: 0,
      _lastBoundsSig: null,
      _cachedPixels: null,
      _cachedTW: 0, _cachedTH: 0, _cachedOX: 0, _cachedOY: 0,
    };
    objArray[id] = obj;
  }

  // Resize only when dimensions actually change; always clear
  if (obj.canvas.width !== canvasWidth || obj.canvas.height !== canvasHeight) {
    obj.canvas.width = canvasWidth;
    obj.canvas.height = canvasHeight;
  }
  obj.ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // Update filtered stream only when fraction changes
  // Binary search on sorted normDelay avoids creating a new filtered array
  if (fraction !== obj.lastFraction) {
    let lo = 0, hi = pattern.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (pattern[mid].normDelay <= fraction) lo = mid + 1;
      else hi = mid;
    }
    obj.aniStream = pattern.slice(0, lo);
    obj.lastFraction = fraction;
  }

  obj.patternSize = patternSize;

  // Get the text region for overlap detection.
  // Cache pixel data to avoid expensive getImageData every frame.
  let trimmedData = null, trimmedWidth = 0, trimmedHeight = 0, offsetX = 0, offsetY = 0;

  const boundsSig = textBounds ? `${Math.round(textBounds.x)},${Math.round(textBounds.y)},${Math.round(textBounds.w)},${Math.round(textBounds.h)}` : 'none';
  // Without explicit bounds, refresh periodically (not every frame) so async
  // canvas content changes get picked up without killing performance.
  if (!obj._trimCounter) obj._trimCounter = 0;
  obj._trimCounter++;
  const needsRefresh = textBounds
    ? boundsSig !== obj._lastBoundsSig
    : (!obj._lastBoundsSig || obj._trimCounter % 120 === 0);

  if (needsRefresh) {
    obj._lastBoundsSig = boundsSig;

    if (textBounds) {
      const bx = Math.max(0, Math.floor(textBounds.x));
      const by = Math.max(0, Math.floor(textBounds.y));
      const tcW = textCanvas.width;
      const tcH = textCanvas.height;
      const bw = Math.min(tcW - bx, Math.ceil(textBounds.w + Math.min(0, textBounds.x)));
      const bh = Math.min(tcH - by, Math.ceil(textBounds.h + Math.min(0, textBounds.y)));
      if (bw > 0 && bh > 0) {
        const ctx = textCanvas.getContext('2d', { willReadFrequently: true });
        obj._cachedPixels = ctx.getImageData(bx, by, bw, bh).data;
        obj._cachedTW = bw;
        obj._cachedTH = bh;
        obj._cachedOX = bx;
        obj._cachedOY = by;
      }
    } else {
      const trimResult = trimCanvas(textCanvas);
      if (trimResult?.trimmedCanvas) {
        const tc = trimResult.trimmedCanvas;
        obj._cachedPixels = tc.getContext('2d', { willReadFrequently: true })
          .getImageData(0, 0, tc.width, tc.height).data;
        obj._cachedTW = tc.width;
        obj._cachedTH = tc.height;
        obj._cachedOX = trimResult.offsetX;
        obj._cachedOY = trimResult.offsetY;
      }
    }
  }

  trimmedData = obj._cachedPixels || null;
  trimmedWidth = obj._cachedTW || 0;
  trimmedHeight = obj._cachedTH || 0;
  offsetX = obj._cachedOX || 0;
  offsetY = obj._cachedOY || 0;

  // Track cumulative fraction so each cell gets an individual lifespan
  // instead of all cells dying at once when the loop resets
  if (obj.lastRawFraction > 0.8 && fraction < 0.2) {
    obj.cycleCount++;
  }
  obj.lastRawFraction = fraction;
  const cumulativeFraction = obj.cycleCount + fraction;

  // Overlap source contexts
  const textCtx = textCanvas.getContext('2d', { willReadFrequently: true });

  // 1) Expire old cells FIRST so their slots are freed before populate
  const drawStream = obj.localDrawStream;
  const CELL_LIFESPAN = 1.0; // each cell lives for one full cycle duration

  for (const key in drawStream) {
    const age = cumulativeFraction - drawStream[key].birthFraction;
    if (age > CELL_LIFESPAN) {
      delete drawStream[key];
      obj.activeBuffer.delete(key);
    }
  }

  // 2) Populate — expired cells can now be immediately re-added
  for (const cell of obj.aniStream) {
    const key = cell._key || `${cell.i}-${cell.j}`;
    if (obj.activeBuffer.has(key)) continue;

    const shapeData = computeShapeBoundingBox(cell, canvasWidth, canvasHeight);
    const { x, y, w, h } = shapeData;

    const overlaps = trimmedData
      ? doesOverlapTrimmedText(trimmedData, trimmedWidth, trimmedHeight, x, y, w, h, offsetX, offsetY)
      : doesOverlapText(textCtx, x, y, w, h);

    if (overlaps) {
      cell.color = brushColor;
      obj.activeBuffer.add(key);
      if (typeof window !== 'undefined' && window.cellScale) {
        cell.scale = window.cellScale;
      }
      drawStream[key] = {
        cell,
        ...shapeData,
        overlap: overlaps,
        birthFraction: cumulativeFraction,
      };
    }
  }

  // 3) Draw all active cells
  obj.ctx.fillStyle = brushColor;
  for (const key in drawStream) {
    drawShape(obj.ctx, drawStream[key]);
  }

  return obj.canvas;
}

/**
 * Invalidate the trim cache for a specific id.
 * Currently a no-op since trimming happens every frame (text moves due to bounce),
 * but kept as a stable export for callers.
 */
export function invalidateTrimCache(id) {
  // Trimming now happens every frame, so no cache to invalidate
}

/**
 * Reset the animation state for a specific id.
 * Clears activeBuffer, localDrawStream, and animation index so the next
 * frame starts fresh with no remnant cells from a previous text/glyph.
 * Used by FontExportNode between character captures.
 */
export function resetAnimationState(id) {
  const obj = objArray[id];
  if (!obj) return;
  obj.activeBuffer.clear();
  obj.localDrawStream = {};
  obj.aniStream = [];
  obj.prevAniStream = [];
  obj.laststreamSize = null;
  obj.lastFraction = null;
  obj.aniIndex = 0;
  obj.cycleCount = 0;
  obj.lastRawFraction = 0;
  obj.ctx.clearRect(0, 0, obj.canvas.width, obj.canvas.height);
}

function computeShapeBoundingBox(cell, canvasWidth, canvasHeight) {
  const x = cell.cellWidth ? cell.x : cell.x * canvasWidth;
  const y = cell.cellWidth ? cell.y : cell.y * canvasHeight;
  const size = cell.cellWidth || canvasWidth / (typeof window !== 'undefined' && window.gridCount ? window.gridCount : cell.totalCount);
  return {
    x,
    y,
    w: size,
    h: size,
    shape: cell.shape ?? (typeof window !== 'undefined' ? window.shapeType : undefined),
    scale: cell.scale,
    customCanvas: cell.customCanvas,
    strokes: cell.strokes,
  };
}

/* ===========================
   Drawing functions
   =========================== */

const drawFunctions = {
  circle(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI);
    ctx.fill();
  },

  // Custom now uses either a provided canvas/image or vector "strokes"
  custom(ctx, { x, y, w, h, color, customCanvas, strokes }) {
    // 1) Raster content provided (Canvas/Image/Video)
    if (customCanvas) {
      try {
        ctx.save();
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(customCanvas, x, y, w, h);
        ctx.restore();
        return;
      } catch (e) {
        // fall through to strokes / fallback
      }
    }

    if (!Array.isArray(strokes) || !strokes.length) {
      // Fallback (never invisible)
      ctx.beginPath();
      ctx.arc(x + w/2, y + h/2, Math.min(w,h)/2, 0, Math.PI*2);
      if (color) ctx.fillStyle = color;
      ctx.fill();
      return;
    }

    // Detect polyline format: [{points:[{x,y}...], widthNorm, color}, ...]
    const looksPolyline = !!strokes[0]?.points;
    if (looksPolyline) {
      ctx.save();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      for (const seg of strokes) {
        const pts = seg.points || [];
        if (pts.length < 2) continue;
        const strokeCol = seg.color || color || '#000';
        const lw = (seg.widthNorm ?? seg.width ?? 0.02) * Math.min(w, h);

        ctx.strokeStyle = strokeCol;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.moveTo(x + pts[0].x * w, y + pts[0].y * h);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(x + pts[i].x * w, y + pts[i].y * h);
        }
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    // Path-segment format: {m:[x,y]} / {l:[x,y]} / {q:[...]} / {b:[...]}
    ctx.save();
    if (color) ctx.fillStyle = color;
    ctx.beginPath();
    for (const seg of strokes) {
      if (seg.m) ctx.moveTo(x + seg.m[0] * w, y + seg.m[1] * h);
      else if (seg.l) ctx.lineTo(x + seg.l[0] * w, y + seg.l[1] * h);
      else if (seg.q) {
        ctx.quadraticCurveTo(
          x + seg.q[0] * w, y + seg.q[1] * h,
          x + seg.q[2] * w, y + seg.q[3] * h
        );
      } else if (seg.b) {
        ctx.bezierCurveTo(
          x + seg.b[0] * w, y + seg.b[1] * h,
          x + seg.b[2] * w, y + seg.b[3] * h,
          x + seg.b[4] * w, y + seg.b[5] * h
        );
      }
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },

  triangle(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x, y + h);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x + w / 2, y);
    ctx.closePath();
    ctx.fill();
  },

  hexagon(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const px = cx + r * Math.cos(angle);
      const py = cy + r * Math.sin(angle);
      i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  },

  asciiArt(ctx, { x, y, w, h, color }) {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const asciiChars = ['@', '#', '&', '%', '*', '+', '=', '-', ':', '.'];
    const index = Math.floor(((x + y) * 7) % asciiChars.length);
    const char = asciiChars[index];
    ctx.save();
    ctx.font = `${Math.floor(h * 0.8)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (color) ctx.fillStyle = color;
    ctx.fillText(char, cx, cy);
    ctx.restore();
  },

  taperedStroke(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 0.9;
    const maxWidth = h * 0.35;
    const angle = ((x * 7 + y * 3) % 360) * (Math.PI / 180);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(-length / 2, 0);
    ctx.lineTo(0, -maxWidth / 2);
    ctx.lineTo(length / 2, 0);
    ctx.lineTo(0, maxWidth / 2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },

  brushStroke(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 0.9;
    const width = h * 0.35;
    const angle = ((x + y) % 360) * (Math.PI / 180);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.ellipse(0, 0, length / 2, width / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  },

  scribblyStroke(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 0.9;
    const maxWidth = h * 0.35;
    const angle = ((x * 7 + y * 3) % 360) * (Math.PI / 180);
    const jitter = (amt) => (Math.random() - 0.5) * amt;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(-length / 2 + jitter(2), jitter(2));
    ctx.lineTo(jitter(2), -maxWidth / 2 + jitter(2));
    ctx.lineTo(length / 2 + jitter(2), jitter(2));
    ctx.lineTo(jitter(2), maxWidth / 2 + jitter(2));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },

  roundedBrush(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 0.9;
    const width = h * 0.4;
    const angle = ((x * 13 + y * 17) % 360) * (Math.PI / 180);
    const r = width / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(-length / 2 + r, -r);
    ctx.arcTo(length / 2 - r, -r, length / 2 - r, r, r);
    ctx.arcTo(length / 2 - r, r, -length / 2 + r, r, r);
    ctx.arcTo(-length / 2 + r, r, -length / 2 + r, -r, r);
    ctx.arcTo(-length / 2 + r, -r, length / 2 - r, -r, r);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },

  expressiveBrush(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 1.9;
    const width = h * 0.4;
    const angle = ((x * 13 + y * 7) % 360) * (Math.PI / 180);
    const r = width / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(-length / 2 + r, -r);
    ctx.arcTo(length / 2 - r, -r, length / 2 - r, r, r);
    ctx.arcTo(length / 2 - r, r, -length / 2 + r, r, r);
    ctx.arcTo(-length / 2 + r, r, -length / 2 + r, -r, r);
    ctx.arcTo(-length / 2 + r, -r, length / 2 - r, -r, r);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },

  warpBrush(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 1.2;
    const radius = h * 0.9;
    const tailWidth = radius * 0.5;
    const angle = ((x * 1 + y * 2) % 360) * (Math.PI / 180);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.arc(-length / 2 + radius, 0, radius, Math.PI / 2, -Math.PI / 2, true);
    ctx.quadraticCurveTo(0, -tailWidth, length / 2, 0);
    ctx.quadraticCurveTo(0, tailWidth, -length / 2 + radius, radius);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  },

  softBrush(ctx, { x, y, w, h, color }) {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const length = w * 0.9;
    const width = h * 0.4;
    const angle = ((x * 13 + y * 17) % 360) * (Math.PI / 180);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, length / 2);
    gradient.addColorStop(0, color || '#000');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(0, 0, length / 2, width / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  },

  wigglyCircle(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const radius = Math.min(w, h) / 2;
    const points = 64;
    const wobble = radius * 0.15;
    ctx.beginPath();
    for (let i = 0; i <= points; i++) {
      const angle = (Math.PI * 2 * i) / points;
      const pressure = 1 + 0.3 * Math.sin(angle * 2);
      const rOffset = Math.sin(i * 0.9 + (x + y)) * wobble * pressure;
      const r = radius + rOffset;
      const px = cx + r * Math.cos(angle);
      const py = cy + r * Math.sin(angle);
      i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  },

  square(ctx, { x, y, w, h, color }) {
    if (color) ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);
  },
};

function getDrawFunction(shape) {
  const map = {
    'ascii-art': 'asciiArt',
    'tapered-stroke': 'taperedStroke',
    'brush-stroke': 'brushStroke',
    'scribbly-stroke': 'scribblyStroke',
    'rounded-brush': 'roundedBrush',
    'expressive-brush': 'expressiveBrush',
    'warp-brush': 'warpBrush',
    'soft-brush': 'softBrush',
    custom: 'custom',
    'wiggly-circle': 'wigglyCircle',
    square: 'square',
  };
  return drawFunctions[map[shape] || shape] || drawFunctions.circle;
}

export function drawShape(ctx, dat /*, brushColor */) {
  const { shape, x, y, w, h, cell } = dat;

  // Safe scale default (window.ani_scale takes precedence if defined)
  const scale =
    (typeof window !== 'undefined' && window.ani_scale != null)
      ? window.ani_scale
      : (dat.scale ?? 1);

  const nW = w * scale;
  const nH = h * scale;
  const offsetX = x + (w - nW) / 2;
  const offsetY = y + (h - nH) / 2;

  const fn = getDrawFunction(shape);
  fn(ctx, {
    x: offsetX,
    y: offsetY,
    w: nW,
    h: nH,
    color: cell?.color,
    // forward custom payloads so 'custom' can render
    customCanvas: dat.customCanvas ?? cell?.customCanvas ?? (typeof window !== 'undefined' ? window.customCanvas : undefined),
    strokes: dat.strokes ?? cell?.strokes ?? (typeof window !== 'undefined' ? (window.__custom_strokes_norm || window.strokes) : undefined),
  });
}

/**
 * Trim a canvas to non-transparent pixels.
 * Returns { trimmedCanvas, offsetX, offsetY } or { trimmedCanvas: null, ... } if empty.
 */
function trimCanvas(canvas) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  let minX = width, minY = height, maxX = 0, maxY = 0;
  let found = false;

  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      const alpha = data[row + x * 4 + 3];
      if (alpha > 0) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!found) return { trimmedCanvas: null, offsetX: 0, offsetY: 0 };

  const trimmedCanvas = document.createElement('canvas');
  const trimmedCtx = trimmedCanvas.getContext('2d');
  const trimmedWidth = maxX - minX + 1;
  const trimmedHeight = maxY - minY + 1;
  trimmedCanvas.width = trimmedWidth;
  trimmedCanvas.height = trimmedHeight;
  trimmedCtx.putImageData(ctx.getImageData(minX, minY, trimmedWidth, trimmedHeight), 0, 0);

  return { trimmedCanvas, offsetX: minX, offsetY: minY };
}

/**
 * Simple bounding-box overlap test. Works even when text extends beyond canvas edges.
 */
function doesOverlapBox(tb, x, y, w, h) {
  return x + w > tb.x && x < tb.x + tb.w && y + h > tb.y && y < tb.y + tb.h;
}

/**
 * Overlap test against trimmed image data (fast).
 * (x, y, boxW, boxH) are in original canvas coords; offX/offY position the trimmed subset.
 */
function doesOverlapTrimmedText(data, w, h, x, y, boxW, boxH, offX, offY) {
  const sx = Math.floor(x - offX), sy = Math.floor(y - offY);
  const ex = Math.ceil(sx + boxW), ey = Math.ceil(sy + boxH);

  let visibleCount = 0;
  const totalPixels = Math.max(1, (ex - sx) * (ey - sy));
  // Scale threshold: small cells need a higher % to avoid antialiasing triggering them.
  // At 17px cells (tool-size), 0.5% ≈ 1.5 pixels. At 4px cells, use ~6% ≈ 1 pixel.
  const pct = totalPixels < 100 ? 0.06 : totalPixels < 400 ? 0.02 : 0.005;
  const threshold = Math.max(2, Math.floor(totalPixels * pct));
  // Higher alpha threshold for small cells to reject antialiasing
  const alphaMin = totalPixels < 100 ? 80 : totalPixels < 400 ? 40 : 10;

  for (let i = sy; i < ey; i++) {
    for (let j = sx; j < ex; j++) {
      if (j < 0 || i < 0 || j >= w || i >= h) continue;
      const idx = (i * w + j) * 4 + 3; // alpha channel
      if (data[idx] > alphaMin) {
        visibleCount++;
        if (visibleCount >= threshold) return true;
      }
    }
  }
  return false;
}

/**
 * Fallback overlap test using the original text canvas.
 * Samples pixels to avoid huge getImageData calls on large rects.
 */
function doesOverlapText(ctx, x, y, w, h) {
  // Clamp rect to canvas bounds
  const cw = ctx.canvas.width;
  const ch = ctx.canvas.height;
  const sx = Math.max(0, Math.floor(x));
  const sy = Math.max(0, Math.floor(y));
  const ex = Math.min(cw, Math.ceil(x + w));
  const ey = Math.min(ch, Math.ceil(y + h));
  const rw = Math.max(0, ex - sx);
  const rh = Math.max(0, ey - sy);
  if (!rw || !rh) return false;

  // For small rects, read directly; for larger, sample grid
  const MAX_PIXELS = 4000;
  const totalPixels = rw * rh;
  const alphaMin = totalPixels < 100 ? 80 : totalPixels < 400 ? 40 : 10;
  const pct = totalPixels < 100 ? 0.06 : totalPixels < 400 ? 0.02 : 0.005;
  const threshold = Math.max(2, Math.floor(totalPixels * pct));

  if (totalPixels <= MAX_PIXELS) {
    const data = ctx.getImageData(sx, sy, rw, rh).data;
    let count = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > alphaMin) { count++; if (count >= threshold) return true; }
    }
    return false;
  } else {
    // Sample a 40x40 grid across the rect
    const steps = 40;
    let count = 0;
    for (let yi = 0; yi < steps; yi++) {
      for (let xi = 0; xi < steps; xi++) {
        const px = Math.floor(sx + (xi / (steps - 1)) * (rw - 1));
        const py = Math.floor(sy + (yi / (steps - 1)) * (rh - 1));
        const pixel = ctx.getImageData(px, py, 1, 1).data;
        if (pixel[3] > alphaMin) { count++; if (count >= threshold) return true; }
      }
    }
    return false;
  }
}
